// Configuration dictionary (OR-10; PD-055, PD-056): visit purposes and organisation display formats.
import { z } from 'zod';

const Uuid = z.uuid();
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Label = z.object({ en: z.string().trim().min(1, 'v.required').max(120, 'v.tooLong'), ar: z.string().trim().max(120, 'v.tooLong').optional() });
const Categories = z.array(Key).min(1).max(50).nullable();

export const CreateVisitPurposeRequest = z.object({
  /** Omitted or null = every site (needs organisation configuration rights). */
  siteId: Uuid.nullable().optional(),
  code: Key,
  label: Label,
  categoryCodes: Categories.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export const UpdateVisitPurposeRequest = z.object({
  expectedVersion: z.number().int().min(1),
  label: Label.optional(),
  categoryCodes: Categories.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  /** false retires the value (never deleted); true brings it back. */
  active: z.boolean().optional(),
});
export const VisitPurposeQuery = z.object({
  siteId: Uuid.optional(),
  categoryCode: Key.optional(),
  /** Administration view: every value in scope, including retired ones. */
  all: z.enum(['true', 'false']).optional(),
});

export interface VisitPurposeResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly code: string;
  readonly label: { readonly en: string; readonly ar?: string | undefined };
  readonly categoryCodes: readonly string[] | null;
  readonly sortOrder: number;
  readonly active: boolean;
  readonly version: number;
  readonly retiredAt: string | null;
  /** Visits that hold this value (history is kept when it is retired). */
  readonly usedBy: number;
}

export const UpdateDisplayFormatRequest = z.object({
  expectedVersion: z.number().int().min(0),
  dateStyle: z.enum(['medium', 'long', 'numeric', 'iso']),
  hourCycle: z.enum(['locale', 'h23', 'h12']),
});
export interface DisplayFormatResponse {
  readonly dateStyle: 'medium' | 'long' | 'numeric' | 'iso';
  readonly hourCycle: 'locale' | 'h23' | 'h12';
  /** 0 = organisation defaults (nothing saved yet). */
  readonly version: number;
}

/** One row of the configuration overview: where each reference list is managed (OR-10). */
export interface ConfigurationAreaResponse {
  readonly key: 'sites' | 'categories' | 'purposes' | 'id_types' | 'qr' | 'session' | 'display';
  readonly route: string | null;
  readonly summary: Readonly<Record<string, string | number | null>>;
  readonly canManage: boolean;
}
export interface ConfigurationOverviewResponse {
  readonly areas: readonly ConfigurationAreaResponse[];
}

// ------------------------------------------------------------------ reception desks (OR-16, PD-057)
export const SetOperatorDeskRequest = z.object({
  siteId: Uuid,
  /** null = leave the desk (no current desk at any site). */
  locationId: Uuid.nullable(),
});
export interface OperatorDeskResponse {
  readonly current: { readonly siteId: string; readonly desk: { readonly id: string; readonly name: string }; readonly since: string } | null;
  /** Active desk locations at the requested site. */
  readonly desks: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

// ------------------------------------------------------------------ operating mode (PB-02-01, PB-02-07; PD-059)
export type IntegrationFamily = 'access_control' | 'sso' | 'directory' | 'identity_verification' | 'calendars' | 'presence_feeds' | 'other_connectors' | 'messaging';
export interface OperatingModeResponse {
  /** standalone = no live external integration is relied on; simulators and local outboxes do not count. */
  readonly mode: 'standalone' | 'integrated';
  readonly families: ReadonlyArray<{
    readonly key: IntegrationFamily;
    /** Active connections to a real (non-simulator) system. */
    readonly live: number;
    /** Active simulators or development adapters. */
    readonly simulated: number;
    /** Messaging only: last 7 days, accepted vs blocked/failed (provider unavailable is never "sent"). */
    readonly recent?: { readonly accepted: number; readonly notSent: number };
  }>;
}

// ------------------------------------------------------------------ arrival guidance (PB-13-09, PD-060)
const GuidanceText = z.object({ en: z.string().trim().min(1, 'v.required').max(300, 'v.tooLong'), ar: z.string().trim().max(300, 'v.tooLong').optional() });
export const GUIDANCE_FIELD_KEYS = ['gate', 'parking', 'building', 'floor', 'route', 'contact'] as const;
export const PublishGuidanceRequest = z.object({
  /** Narrow to one visitor type; null = every type. */
  categoryCode: Key.nullable(),
  /** Narrow to a destination subtree at this site; null = the whole site. */
  locationId: Uuid.nullable(),
  fields: z
    .object({ gate: GuidanceText.optional(), parking: GuidanceText.optional(), building: GuidanceText.optional(), floor: GuidanceText.optional(), route: GuidanceText.optional(), contact: GuidanceText.optional() })
    .refine((f) => Object.values(f).some(Boolean), 'v.guidanceEmpty'),
  note: z.string().trim().max(500, 'v.tooLong').nullable().optional(),
});
export const RetireGuidanceRequest = z.object({ expectedStatus: z.literal('published') });
export type GuidanceFieldsDto = Partial<Record<(typeof GUIDANCE_FIELD_KEYS)[number], { readonly en: string; readonly ar?: string | undefined }>>;
export interface GuidanceVersionResponse {
  readonly id: string;
  readonly categoryCode: string | null;
  readonly location: { readonly id: string; readonly name: string } | null;
  readonly fields: GuidanceFieldsDto;
  readonly number: number;
  readonly note: string | null;
  readonly status: 'published' | 'retired';
  readonly publishedAt: string;
  readonly retiredAt: string | null;
}
/** What a visitor or visit reader sees: the resolved guidance in force now. */
export interface ResolvedGuidanceDto {
  readonly fields: GuidanceFieldsDto;
}
